import GridView, { type GridViewProps } from "@common/controls/GridView/component";
import Hint from "@common/controls/Hint/component";
import { useI18n } from "@common/hooks/i18n";
import { sort } from "@common/lib/array";
import date from "@common/lib/date";
import { formatMoneyDisplay } from "@common/lib/format";
import { isNullOrUndefined } from "@common/lib/misc";
import Network from "@common/modules/network/types";
import * as React from "react";
import { isValidPeriodHidden } from "./lib";

type VolumeOrderDrillDownGridProps = {
  data: Network.CommissionRules.Api.GenealogyHierarchyUserVolume[];
  orderId: number;
  volumeTypeLabel: string;
};

export const VolumeOrderDrillDownGrid = (props: VolumeOrderDrillDownGridProps) => {
  const i18n = useI18n();

  const volumeOrderDrillDownGridData = React.useMemo((): GridViewProps<
    Network.CommissionRules.Api.GenealogyHierarchyUserVolume,
    Network.CommissionRules.Api.GenealogyHierarchyUserVolume
  > => {
    const items = props.data.filter((x) => x.orderNumber === props.orderId);

    return {
      idKey: "productVariantName",
      columns: [
        {
          key: "productVariantName",
          name: i18n.name,
          dataType: "string",
          data: (x) => {
            return (
              <>
                {x.productName ?? x.description} <Hint hint={x.productVariantName} />
              </>
            );
          },
          visibility: () =>
            items.some(
              (x) =>
                !isNullOrUndefined(x.productName) ||
                !isNullOrUndefined(x.description) ||
                !isNullOrUndefined(x.productVariantName)
            ),
          qa: "Name"
        },
        {
          key: "startCycleEndDate",
          name: i18n.validPeriod,
          dataType: "string",
          data: (x) => `${date.formatDate(x.startCycleEndDate)} - ${date.formatDate(x.endCycleEndDate)}`,
          visibility: () => !isValidPeriodHidden(props.data),
          qa: "Valid period"
        },
        {
          key: "quantity",
          name: i18n.quantity,
          dataType: "number"
        },
        {
          key: "volume",
          name: props.volumeTypeLabel,
          dataType: "string",
          data: (x) => formatMoneyDisplay(x.volume * x.quantity, x.currencyCode ?? undefined)
        }
      ],
      data: sort(items, { key: "productVariantName" }),
      isLoading: false,
      toolbar: {
        showAdd: false,
        showSearch: false
      }
    };
  }, [i18n, props.volumeTypeLabel, props.orderId]);

  return (
    <GridView
      pageSize={10}
      {...volumeOrderDrillDownGridData}
      idKey="productVariantName"
      isLoading={false}
      useSearchUrl={false}
    />
  );
};
